import { useChatActions, useChatStoreApi } from '@ai-sdk-tools/store';
import { useMutation } from '@apollo/client';
import {
  MessageFeedbackReason,
  MessageFeedbackStage,
  type UpsertMessageFeedbackInput,
} from '@newfront-insurance/ai-api';
import { cn, PopoverContent } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { graphql } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  FeedbackReasonButton,
  FeedbackReasonGroup,
  FEEDBACK_ACTION_GRADIENT,
  FEEDBACK_POPOVER_POSITION,
  FEEDBACK_POPOVER_SHELL_CLASS_NAME,
} from './feedback-controls';
import type { UIMessageWithMetadata } from '../../../lib/types';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useNovaContext } from '../hooks/use-chat-context';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';
import { NOVA_FEEDBACK_DETAIL_MAX_LENGTH } from '../lib/feedback-api-contract';
import { buildNovaRequestContext } from '../lib/request-context';

const UPSERT_MESSAGE_FEEDBACK = graphql(`
  mutation UpsertNovaMessageFeedback($messageUuid: String!, $input: MessageFeedbackInput!) {
    upsertMessageFeedback(messageUuid: $messageUuid, input: $input) {
      uuid
      messageUuid
      reason
      stage
      detail
    }
  }
`);

const FEEDBACK_REASONS = [
  { value: MessageFeedbackReason.SOMETHING_BROKE, label: 'Something broke', startsRegeneration: false },
  { value: MessageFeedbackReason.INCOMPLETE, label: 'Incomplete', startsRegeneration: true },
  {
    value: MessageFeedbackReason.SOURCES_WRONG_OR_MISSING,
    label: 'Sources wrong or missing',
    startsRegeneration: true,
  },
  {
    value: MessageFeedbackReason.DIDNT_FOLLOW_INSTRUCTIONS,
    label: "Didn't follow instructions",
    startsRegeneration: true,
  },
  { value: MessageFeedbackReason.OUTDATED, label: 'Outdated', startsRegeneration: true },
  { value: MessageFeedbackReason.INACCURATE, label: 'Inaccurate', startsRegeneration: true },
  { value: MessageFeedbackReason.OTHER, label: 'Other', startsRegeneration: true },
] as const;

interface MessageFeedbackPopoverProps {
  message: UIMessageWithMetadata;
  open: boolean;
  regenerationEnabled: boolean;
  isReactionSaving: boolean;
  onOpenChange: (open: boolean) => void;
  waitForReactionPersistence: () => Promise<void>;
}

export function MessageFeedbackPopover({
  message,
  open,
  regenerationEnabled,
  isReactionSaving,
  onOpenChange,
  waitForReactionPersistence,
}: MessageFeedbackPopoverProps) {
  const firstReasonRef = useRef<HTMLButtonElement>(null);
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [selectedReason, setSelectedReason] = useState<MessageFeedbackReason>();
  const [detail, setDetail] = useState('');
  const [isSavingReason, setIsSavingReason] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [upsertFeedback] = useMutation(UPSERT_MESSAGE_FEEDBACK, {
    context: { notifications: { preventErrorNotification: true } },
  });
  const { regenerate } = useChatActions<UIMessageWithMetadata>();
  const chatStoreApi = useChatStoreApi<UIMessageWithMetadata>();
  const { accountUuid, classUuid } = useNovaContext();
  const overlay = useChatOverlayContext();
  const notifications = useNotifications();
  const analytics = useAnalytics();
  const activeReason = selectedReason;
  const activeReasonConfig = FEEDBACK_REASONS.find((reason) => reason.value === activeReason);
  const shouldRegenerate = regenerationEnabled && activeReasonConfig?.startsRegeneration === true;
  const detailLabel = shouldRegenerate ? 'What would make it better? (optional)' : 'What happened? (optional)';
  const detailPlaceholder = shouldRegenerate
    ? 'Nova will rewrite this answer using your feedback.'
    : 'Tell us what went wrong with this answer.';
  const submitLabel = shouldRegenerate ? 'Fix this answer' : 'Report problem';

  const persistFeedback = async (reason: MessageFeedbackReason, stage: MessageFeedbackStage, nextDetail?: string) => {
    await waitForReactionPersistence();
    const input = {
      reason,
      stage,
      detail: nextDetail?.trim() || null,
    } satisfies UpsertMessageFeedbackInput;
    const result = await upsertFeedback({
      variables: {
        messageUuid: message.id,
        input,
      },
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
    return result;
  };

  const captureReason = (reason: MessageFeedbackReason) => {
    setSelectedReason(reason);
    setDetail('');
    setError(undefined);
    setIsSavingReason(true);
    const save = saveQueueRef.current
      .catch(() => undefined)
      .then(() => persistFeedback(reason, MessageFeedbackStage.CAPTURED))
      .then(() => {
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REASON_CAPTURED, {
          messageUuid: message.id,
          reason,
        });
      })
      .catch(() => setError("We couldn't save that selection. Try again."))
      .finally(() => setIsSavingReason(false));
    saveQueueRef.current = save;
  };

  const handleSubmit = async () => {
    if (!activeReason || isSubmitting) return;

    setError(undefined);
    setIsSubmitting(true);
    try {
      await saveQueueRef.current.catch(() => undefined);
      const result = await persistFeedback(activeReason, MessageFeedbackStage.SUBMITTED, detail);
      const feedbackUuid = result.data?.upsertMessageFeedback.uuid;
      if (!feedbackUuid) throw new Error('Feedback save returned no identifier');

      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_SUBMITTED, {
        hasDetail: Boolean(detail.trim()),
        messageUuid: message.id,
        reason: activeReason,
      });

      if (!shouldRegenerate) {
        notifications.add({
          type: 'success',
          duration: 3000,
          title: "Thanks, we're on it. We'll email you when it's fixed.",
        });
        onOpenChange(false);
        return;
      }

      onOpenChange(false);
      const startedAt = performance.now();
      const targetChatUuid = overlay.chatUuid;
      const messagesBeforeRegeneration = chatStoreApi.getState().messages;
      let messagesRestored = false;
      try {
        const payloadAdditionalContext = Object.fromEntries(
          Object.entries(overlay.additionalContext ?? {}).filter(([key]) => key !== 'messageMetadata'),
        );
        const requestContext = await buildNovaRequestContext({
          accountUuid,
          additionalContext: payloadAdditionalContext,
          classUuid,
        });

        overlay.beginRegeneration(targetChatUuid, message);
        let streamError: unknown;
        try {
          await regenerate({
            messageId: message.id,
            body: {
              ...requestContext,
              regeneration: {
                targetMessageUuid: message.id,
                feedbackUuid,
              },
            },
          });
        } catch (caughtError) {
          streamError = caughtError;
        }

        // The external store mirrors the AI SDK state on a later frame and can still contain
        // an error from an earlier request. The durable backend snapshot is authoritative, and
        // also lets us recover when the stream disconnects after the variant was persisted.
        const canonicalMessages = await overlay.refetchMessages(targetChatUuid);
        if (canonicalMessages && overlay.isActiveChat(targetChatUuid)) {
          const chatState = chatStoreApi.getState();
          chatState.clearTransientDataParts();
          chatState.setNewChat(targetChatUuid, canonicalMessages);
          chatState.setMessages(canonicalMessages);
          messagesRestored = true;
        }
        const persistedVariant = canonicalMessages?.some((candidate) => {
          const regeneration = candidate.metadata?.regeneration;
          return regeneration?.sourceMessageUuid === message.id && regeneration.feedbackUuid === feedbackUuid;
        });
        if (!canonicalMessages || !persistedVariant) {
          throw streamError ?? new Error('Regenerated answer was not present in the canonical chat snapshot');
        }
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REGENERATION_SUCCEEDED, {
          latencyMs: Math.round(performance.now() - startedAt),
          messageUuid: message.id,
          reason: activeReason,
        });
      } catch {
        if (!messagesRestored && overlay.isActiveChat(targetChatUuid)) {
          const chatState = chatStoreApi.getState();
          chatState.clearTransientDataParts();
          chatState.setNewChat(targetChatUuid, messagesBeforeRegeneration);
          chatState.setMessages(messagesBeforeRegeneration);
        }
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REGENERATION_FAILED, {
          messageUuid: message.id,
          reason: activeReason,
        });
      } finally {
        overlay.clearPendingRegeneration(targetChatUuid);
      }
    } catch {
      setError(shouldRegenerate ? 'Feedback could not be saved.' : 'The problem report could not be sent. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <PopoverContent
      data-testid="nova-feedback-popover"
      role="dialog"
      aria-label="What went wrong?"
      aria-modal="false"
      {...FEEDBACK_POPOVER_POSITION}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        firstReasonRef.current?.focus();
      }}
      className={cn(FEEDBACK_POPOVER_SHELL_CLASS_NAME, 'w-[min(28.75rem,calc(100vw-2rem))]')}
    >
      <div className="p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm leading-5 font-semibold text-black">What went wrong?</h3>
          <span className="text-steel-400 inline-flex items-center gap-1 text-xs" role="status">
            {(isReactionSaving || isSavingReason) && (
              <>
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                Saving
              </>
            )}
          </span>
        </div>
        <FeedbackReasonGroup className="mt-2.5 flex flex-wrap gap-x-0.5 gap-y-2.5" aria-label="Feedback reason">
          {FEEDBACK_REASONS.map((reason, index) => (
            <FeedbackReasonButton
              key={reason.value}
              data-testid="nova-feedback-reason"
              data-reason={reason.value}
              ref={index === 0 ? firstReasonRef : undefined}
              selected={activeReason === reason.value}
              onClick={() => captureReason(reason.value)}
            >
              {reason.label}
            </FeedbackReasonButton>
          ))}
        </FeedbackReasonGroup>
      </div>

      {activeReason && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="border-steel-200 mt-2.5 border-t p-4"
        >
          <label htmlFor={`nova-feedback-detail-${message.id}`} className="text-sm leading-5 font-semibold text-black">
            {detailLabel}
          </label>
          <textarea
            id={`nova-feedback-detail-${message.id}`}
            value={detail}
            rows={3}
            maxLength={NOVA_FEEDBACK_DETAIL_MAX_LENGTH}
            onChange={(event) => {
              setDetail(event.target.value);
              setError(undefined);
            }}
            placeholder={detailPlaceholder}
            className={cn(
              'border-steel-200 text-steel-500 placeholder:text-steel-300 mt-2.5 h-[6.25rem] w-full resize-none',
              'focus:border-steel-400 rounded-[0.75rem] border p-3 text-sm leading-5 outline-none',
            )}
          />
          {error && (
            <div className="mt-2 flex items-center justify-between gap-3" role="alert">
              <p className="text-fire-600 text-xs">{error}</p>
              {activeReason && error.includes("couldn't save") && (
                <button
                  type="button"
                  className="text-brand-400 text-xs font-medium"
                  onClick={() => captureReason(activeReason)}
                >
                  Retry
                </button>
              )}
            </div>
          )}
          <div className="mt-2.5 flex justify-end">
            <button
              data-testid="nova-feedback-submit"
              type="button"
              disabled={isSubmitting || isSavingReason}
              onClick={() => void handleSubmit()}
              className={cn(
                'inline-flex h-9 min-w-[10.375rem] items-center justify-center gap-2 rounded-full px-4',
                'text-sm leading-5 font-semibold text-[#350E5E] transition-opacity hover:opacity-90',
                'disabled:cursor-not-allowed disabled:opacity-50',
                FEEDBACK_ACTION_GRADIENT,
              )}
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRight className="size-4" aria-hidden="true" />
              )}
              {submitLabel}
            </button>
          </div>
        </motion.div>
      )}
    </PopoverContent>
  );
}
